import { useEffect, useRef, useState } from 'react';
import type { Completions } from '@mcpanel/core';
import { api } from '../api/client';

export function commonPrefix(values: string[]) {
  let prefix = values[0] ?? '';
  for (const value of values.slice(1)) while (!value.startsWith(prefix)) prefix = prefix.slice(0, -1);
  return prefix;
}
export function useConsoleCompletions(
  serverId: string,
  input: string,
  enabled: boolean,
  report: (error: unknown) => void,
) {
  const [result, setResult] = useState<{ serverId: string; input: string; data: Completions }>();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(-1);
  const request = useRef(0);
  const current = useRef({ input, enabled, serverId });
  current.current = { input, enabled, serverId };
  const reportRef = useRef(report);
  reportRef.current = report;
  useEffect(() => {
    const generation = ++request.current;
    if (!enabled) {
      setOpen(false);
      setResult(undefined);
      return;
    }
    const timer = setTimeout(() => {
      void api
        .completions(serverId, input)
        .then((data) => {
          if (generation === request.current) setResult({ serverId, input, data });
        })
        .catch((error: unknown) => {
          if (generation === request.current) {
            setResult(undefined);
            setOpen(false);
            reportRef.current(error);
          }
        });
    }, 180);
    return () => {
      clearTimeout(timer);
      request.current++;
    };
  }, [serverId, input, enabled]);
  const load = async () => {
    if (result?.input === input && result.serverId === serverId) return result.data;
    const generation = ++request.current;
    const data = await api.completions(serverId, input);
    if (
      generation !== request.current ||
      current.current.input !== input ||
      !current.current.enabled ||
      current.current.serverId !== serverId
    )
      return undefined;
    setResult({ serverId, input, data });
    return data;
  };
  return {
    data: result?.input === input && result.serverId === serverId ? result.data : undefined,
    open: open && enabled,
    setOpen,
    selected,
    setSelected,
    load,
  };
}
